import type { ConditionalAnswer } from '../../domain/types.js';
import { seededShuffle } from '../../domain/seededRandom.js';
import { SEEDED_PLOT_DEVICE_CATALOG } from '../../domain/plotDeviceCatalog.js';
import { validateOperatingSchedule } from '../../domain/schedule.js';
import type { PlotDevice, WorldDraft, WorldSnapshot } from '../../domain/types.js';
import {
  PLOT_DEVICE_SCHEMA_VERSION,
  WORLD_SNAPSHOT_SCHEMA_VERSION,
} from '../../domain/types.js';
import { validateScheduleStep } from './hoursStep.js';
import { validateGrounding, validateConnected } from './questionSchema.js';
import { validateTimePeriod } from './periodStep.js';

/** Snapshot answers preserve source/status for user, AI, fallback, and skipped — exclude stale/unanswered. */
function snapshotAnswersFromDraft(
  answers: Record<string, ConditionalAnswer>,
): WorldSnapshot['answers'] {
  return Object.fromEntries(
    Object.entries(answers).filter(
      ([, a]) => a.status !== 'stale' && a.status !== 'unanswered',
    ),
  ) as WorldSnapshot['answers'];
}

export function generateLocalDevices(draftId: string, count = 20): PlotDevice[] {
  const picked = seededShuffle(SEEDED_PLOT_DEVICE_CATALOG, draftId).slice(0, count);
  return picked.map((d, i) => ({
    ...d,
    deviceId: `gen-${draftId.slice(-6)}-${String(i + 1).padStart(2, '0')}`,
    schemaVersion: PLOT_DEVICE_SCHEMA_VERSION,
    source: 'generated' as const,
    contextSourceIds: [draftId],
  }));
}

export function buildWorldSnapshot(
  draft: WorldDraft,
  devices: PlotDevice[],
): { ok: true; snapshot: WorldSnapshot } | { ok: false; error: string } {
  if (!draft.location || !draft.connected || !draft.timePeriod || !draft.schedule) {
    return { ok: false, error: 'World draft is incomplete.' };
  }
  const locErr = validateGrounding(draft.location);
  if (locErr) return { ok: false, error: locErr };
  const connErr = validateConnected(draft.connected);
  if (connErr) return { ok: false, error: connErr };
  const periodErr = validateTimePeriod(draft.timePeriod);
  if (periodErr) return { ok: false, error: periodErr };
  const schedErr = validateScheduleStep(draft.schedule, draft.location.region);
  if (schedErr) return { ok: false, error: schedErr };
  const sched = validateOperatingSchedule(draft.schedule);
  if (!sched.valid) return { ok: false, error: sched.errors[0] ?? 'Invalid schedule.' };

  const now = new Date().toISOString();
  const snapshot: WorldSnapshot = {
    schemaVersion: WORLD_SNAPSHOT_SCHEMA_VERSION,
    worldId: `world_${draft.draftId}`,
    version: 1,
    acceptedAt: now,
    draftId: draft.draftId,
    location: draft.location,
    connected: draft.connected,
    answers: snapshotAnswersFromDraft(draft.answers),
    timePeriod: draft.timePeriod,
    schedule: draft.schedule,
    characters: draft.characterDrafts.filter((c) => c.status !== 'removed'),
    characterBonds: draft.characterBonds ?? [],
    devices,
    clock: { inWorldIso: now, episodeNumber: 1 },
    operatingStatus: 'open',
    generationContext: {
      worldId: `world_${draft.draftId}`,
      worldVersion: 1,
      answerIds: Object.keys(draft.answers),
      generationSeed: draft.draftId,
      generatedAt: now,
    },
  };
  return { ok: true, snapshot };
}
